import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";

import type { LucideIcon } from "lucide-react";
import { useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";

export interface DetailTab {
  id: string;
  label: string;
  icon?: LucideIcon;
  /** A small attention pill after the label — a COUNT of things waiting on
   * the person (open reseller applications, say). `null` / `undefined` / `""`
   * renders nothing, so a zero is the caller's decision, not a "0" pill. */
  badge?: string | number | null;
}

interface DetailTabsProps {
  tabs: DetailTab[];
  activeId: string;
  onTabChange: (id: string) => void;
  /** Accessible label for the tablist. */
  ariaLabel?: string;
  className?: string;
}

/** Where the sliding pill sits, in px relative to the tablist. */
interface IndicatorRect {
  left: number;
  width: number;
}

/**
 * Segmented tab control for entity-detail pages (customer / prospect /
 * reseller / licences). Presentational + controlled — the parent owns the
 * active id (usually via `useTabParam`).
 *
 * **Look = the app-wide tab look.** A soft recessed track (`surfaceSoft` +
 * hairline) with the active tab lifted out as a paper pill that SLIDES
 * between tabs. It reproduces exactly what the theme's `MuiTabs` / `MuiTab`
 * override gives every plain MUI `<Tabs>` (`styles/theme.ts`) — this strip
 * is hand-rolled only because it predates that override and carries its own
 * badge + lucide-icon slots. The pill is one absolutely-positioned element
 * measured off the selected button (`offsetLeft` / `offsetWidth`) and
 * re-measured when the list resizes (fonts settling, a label changing), so
 * the buttons themselves stay flat and only the pill animates.
 *
 * It used to be an underline strip — and, because a native `<button>` has a
 * 2px border on ALL sides, its `borderColor: primary.main` painted a full
 * navy BOX around the active tab instead of an underline. Nothing here sets
 * a border on the buttons any more; the pill carries the chrome.
 *
 * Keyboard: roving tabindex + Left/Right arrows move selection (with
 * wraparound), per the WAI-ARIA tabs pattern (automatic activation).
 */
export function DetailTabs({
  tabs,
  activeId,
  onTabChange,
  ariaLabel = "Detail sections",
  className,
}: DetailTabsProps) {
  const listRef = useRef<HTMLElement>(null);
  const [indicator, setIndicator] = useState<IndicatorRect | null>(null);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;
    const measure = (): void => {
      const active = list.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
      if (!active) {
        setIndicator(null);
        return;
      }
      setIndicator({ left: active.offsetLeft, width: active.offsetWidth });
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [activeId, tabs]);

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
    const index = tabs.findIndex((tab) => tab.id === activeId);
    if (index < 0) return;
    event.preventDefault();
    const delta = event.key === "ArrowRight" ? 1 : -1;
    const next = tabs[(index + delta + tabs.length) % tabs.length];
    if (next) onTabChange(next.id);
  };

  return (
    <Box sx={{ display: "flex" }} {...(className ? { className } : {})}>
      <Box
        data-slot="detail-tabs-track"
        sx={{
          display: "inline-flex",
          maxWidth: "100%",
          p: "4px",
          // Concentric with the theme's MuiTabs track: knob + 2 outside,
          // knob − 2 on the pill, 4px of padding between.
          borderRadius: (t) => `${t.shape.borderRadius + 2}px`,
          bgcolor: "surfaceSoft",
          border: 1,
          borderColor: "divider",
        }}
      >
        <Box
          ref={listRef}
          component="nav"
          role="tablist"
          aria-label={ariaLabel}
          sx={{
            position: "relative",
            display: "flex",
            gap: "2px",
            overflowX: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {indicator ? (
            <Box
              aria-hidden
              data-slot="detail-tabs-indicator"
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: indicator.left,
                width: indicator.width,
                borderRadius: (t) => `${Math.max(t.shape.borderRadius - 2, 4)}px`,
                bgcolor: "background.paper",
                border: 1,
                borderColor: "divider",
                boxShadow: 1,
                pointerEvents: "none",
                transition: (t) =>
                  t.transitions.create(["left", "width"], {
                    duration: 200,
                    easing: t.transitions.easing.easeInOut,
                  }),
                "@media (prefers-reduced-motion: reduce)": { transition: "none" },
              }}
            />
          ) : null}
          {tabs.map((tab) => {
            const isActive = tab.id === activeId;
            const Icon = tab.icon;
            return (
              <Box
                key={tab.id}
                component="button"
                type="button"
                role="tab"
                aria-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                onClick={() => onTabChange(tab.id)}
                onKeyDown={handleKeyDown}
                sx={{
                  position: "relative",
                  zIndex: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  whiteSpace: "nowrap",
                  minHeight: 32,
                  px: 1.75,
                  py: 0.75,
                  // No border, ever — the pill behind the button is the chrome.
                  border: 0,
                  borderRadius: (t) => `${Math.max(t.shape.borderRadius - 2, 4)}px`,
                  font: "inherit",
                  fontWeight: isActive ? 600 : 500,
                  lineHeight: 1.2,
                  background: "none",
                  cursor: "pointer",
                  transition: (t) =>
                    t.transitions.create(["color", "background-color"], { duration: 150 }),
                  color: isActive ? "primary.main" : "text.secondary",
                  "&:hover": isActive
                    ? {}
                    : {
                        color: "text.primary",
                        bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
                      },
                  "&:focus-visible": {
                    outline: (t) => `2px solid ${t.palette.primary.main}`,
                    outlineOffset: -2,
                  },
                  '& [data-slot="detail-tab-icon"]': {
                    color: isActive ? "primary.main" : "text.secondary",
                  },
                  '&:hover [data-slot="detail-tab-icon"]': isActive
                    ? {}
                    : { color: "text.primary" },
                }}
              >
                {Icon ? (
                  <Box component="span" sx={{ display: "flex" }} data-slot="detail-tab-icon">
                    <Icon size={16} />
                  </Box>
                ) : null}
                <span>{tab.label}</span>
                {tab.badge !== undefined && tab.badge !== null && tab.badge !== "" ? (
                  <Box
                    component="span"
                    data-slot="detail-tab-badge"
                    data-testid={`detail-tab-badge-${tab.id}`}
                    sx={{
                      ml: 0.5,
                      minWidth: 20,
                      height: 20,
                      px: 0.75,
                      borderRadius: 10,
                      fontSize: 11,
                      fontWeight: 700,
                      lineHeight: "20px",
                      textAlign: "center",
                      bgcolor: "warning.main",
                      color: "warning.contrastText",
                    }}
                  >
                    {tab.badge}
                  </Box>
                ) : null}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
